import classnames from 'classnames';
import React from 'react';
import { Accordion, Card, Tab, Tabs } from 'react-bootstrap';
import ReactCommonmark from 'react-commonmark';
import { BsChevronLeft, BsChevronRight, BsPlusLg, BsXLg } from 'react-icons/bs';
import { useQuery } from 'react-query';
import Slider from 'react-slick';
import { toast } from 'react-toastify';
import FormLabel, { FormLabelProps } from 'v2/form/FormLabel/FormLabel';
import {
  DjangoListResponse,
  GenericObject,
  InstructionEntity,
  StrapiApiError,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import styles from './DescriptionGuidance.module.scss';

interface DescriptionGuidanceProps {
  error: FormLabelProps['error'];
}

interface CreateSearchGuidanceData {
  [key: string]: {
    value: string | string[];
  };
}

const settings = {
  className: '',
  dots: false,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: true,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
};

function NextArrow(props: GenericObject) {
  const { className, onClick } = props;
  const isDisbaled = className.includes('slick-disabled');
  return (
    <div
      className={classnames(styles.nextArrow, {
        [styles.slickArrowDisabled]: isDisbaled,
      })}
      onClick={onClick}
    >
      <BsChevronRight className="text-primary" />
    </div>
  );
}

function PrevArrow(props: GenericObject) {
  const { className, onClick } = props;
  const isDisbaled = className.includes('slick-disabled');

  return (
    <div
      className={classnames(styles.prevArrow, {
        [styles.slickArrowDisabled]: isDisbaled,
      })}
      onClick={onClick}
    >
      <BsChevronLeft className="text-primary" />
    </div>
  );
}

export type CreateSearchGuidanceTabs = {
  eventKey: 'instructions' | 'good_examples' | 'bad_examples';
  title: string;
};
const DescriptionGuidance = ({ error }: DescriptionGuidanceProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const { data } = useQuery(
    'dataProviders',
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<InstructionEntity>
      >('/dj/api/v1/instructions', { view: 'create-search' });

      return response.results.reduce(
        (acc, item) => ({
          ...acc,
          [item.key]: {
            value: item.value,
          },
        }),
        {}
      ) as CreateSearchGuidanceData;
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(
          `Error loading data description instructions. ${error.message || ''}`
        );
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const TABS: CreateSearchGuidanceTabs[] = [
    {
      eventKey: 'instructions',
      title: `instructions`,
    },
    {
      eventKey: 'good_examples',
      title: `good examples`,
    },
    {
      eventKey: 'bad_examples',
      title: `bad examples`,
    },
  ];

  if (!data) {
    return null;
  }

  return (
    <Accordion className={classnames('mb-2', styles.root)}>
      <Card className={classnames('bg-white border-0')}>
        <Card.Header
          className={classnames(
            styles.cardHeader,
            'd-flex justify-content-between align-items-center',
            'p-0 bg-white',
            'border-0'
          )}
        >
          <FormLabel
            label={'Request Description'}
            required={true}
            error={error}
          />
          <Accordion.Toggle
            className={classnames('text-primary', styles.toggle, {
              [`${styles.collapsed} border-0`]: isCollapsed,
              'border border-bottom-0': !isCollapsed,
            })}
            eventKey="0"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <>
                View Guidance
                <BsPlusLg className="ml-2" />
              </>
            ) : (
              <>
                Close Guidance
                <BsXLg className="ml-2" size={12} />
              </>
            )}
          </Accordion.Toggle>
        </Card.Header>
        <Accordion.Collapse eventKey="0">
          <Card.Body className={classnames(styles.cardBody, 'bg-light border')}>
            <Tabs
              className={classnames('border-bottom-0')}
              role="tab"
              transition={false}
            >
              {TABS.map((item) => (
                <Tab
                  key={item.eventKey}
                  className={'border-primary mt-5'}
                  eventKey={item.eventKey}
                  unmountOnExit={true}
                  title={item.title}
                >
                  {item.eventKey === 'instructions' ? (
                    <ReactCommonmark
                      className="text-light4"
                      source={(data.instructions?.value?.[0] || '') as string}
                      softBreak="br"
                    />
                  ) : (
                    <Slider {...settings}>
                      {(data[item.eventKey]?.value as string[])?.map(
                        (dataItem, idx) => (
                          <div key={idx}>
                            <div>
                              <label>Example {idx + 1}</label>
                            </div>
                            <ReactCommonmark
                              className="text-light4"
                              source={dataItem || ''}
                              softBreak="br"
                            />
                          </div>
                        )
                      )}
                    </Slider>
                  )}
                </Tab>
              ))}
            </Tabs>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};

export default DescriptionGuidance;
